/**
 * Skill journeys: one practice trail per skill. Each step names what to practise, shows a reference
 * lesson that teaches the skill and asks for a photo of the child's practice (capture). Captures build
 * mastery and a before / after comparison. Self-authored prompts; steps go from the easiest to the
 * hardest lesson that teaches the skill.
 */
import { t, type L } from '../text';
import type { Drawing, SkillDef, SkillId, SkillJourney } from './model';

const STAGES: L[] = [t('Warm-up', 'Khởi động'), t('Practice', 'Luyện tập'), t('Show it off', 'Trổ tài')];

/** Per skill: one prompt per stage. */
const PROMPTS: Record<SkillId, [L, L, L]> = {
  shapes: [
    t('Draw five circles and five boxes, big and small.', 'Vẽ năm hình tròn và năm hình hộp, to có nhỏ có.'),
    t('Find the shapes hiding in the lesson drawing and sketch them first.', 'Tìm các hình ẩn trong bài vẽ và phác chúng trước.'),
    t('Build a brand new animal only from circles and ovals.', 'Dựng một con vật mới chỉ từ hình tròn và bầu dục.'),
  ],
  patterns: [
    t('Fill a row with dots, then a row with zig-zags.', 'Vẽ một hàng chấm, rồi một hàng zíc-zắc.'),
    t('Decorate the lesson drawing with one pattern you repeat.', 'Trang trí bài vẽ bằng một hoa văn lặp lại.'),
    t('Mix three patterns on one drawing without crowding it.', 'Kết hợp ba hoa văn trên một bức vẽ mà không rối mắt.'),
  ],
  pressure: [
    t('Draw a line that goes from very light to very dark.', 'Vẽ một nét đi từ thật nhạt đến thật đậm.'),
    t('Sketch the lesson lightly, then go over the final lines firmly.', 'Phác nhẹ bài vẽ, rồi tô đậm các nét chính.'),
    t('Make a drawing with three clear grey levels.', 'Vẽ một bức có ba mức xám rõ ràng.'),
  ],
  shading: [
    t('Shade a ball with the light coming from one side.', 'Tô bóng một quả bóng với ánh sáng chiếu từ một phía.'),
    t('Add a shadow side and a cast shadow to the lesson drawing.', 'Thêm mặt tối và bóng đổ cho bài vẽ.'),
    t('Shade a whole scene with one light source.', 'Tô bóng cả một cảnh với một nguồn sáng.'),
  ],
  texture: [
    t('Fill four squares with bumpy, smooth, spiky and soft marks.', 'Tô bốn ô vuông bằng nét sần, mịn, nhọn và mềm.'),
    t('Give the lesson drawing a texture you can almost touch.', 'Cho bài vẽ một chất liệu như sờ được.'),
    t('Draw two objects with very different textures side by side.', 'Vẽ hai đồ vật có chất liệu thật khác nhau cạnh nhau.'),
  ],
  fur: [
    t('Flick short strokes in one direction, like brushing a cat.', 'Vẩy những nét ngắn theo một hướng, như chải lông mèo.'),
    t('Add fur that follows the shape of the lesson animal.', 'Thêm lông theo dáng con vật trong bài.'),
    t('Draw a fluffy friend with long and short fur.', 'Vẽ một bạn lông xù có lông dài và lông ngắn.'),
  ],
  lineOfAction: [
    t('Draw three swoopy lines: a C, an S and a straight one.', 'Vẽ ba nét cong: chữ C, chữ S và một nét thẳng.'),
    t('Start the lesson drawing from one action line.', 'Bắt đầu bài vẽ từ một đường dáng.'),
    t('Draw a character jumping, built on one action line.', 'Vẽ một nhân vật đang nhảy, dựng trên một đường dáng.'),
  ],
  expression: [
    t('Draw four faces: happy, sad, sleepy and surprised.', 'Vẽ bốn gương mặt: vui, buồn, buồn ngủ và ngạc nhiên.'),
    t('Give the lesson character a new feeling.', 'Cho nhân vật trong bài một cảm xúc mới.'),
    t('Tell a tiny story with two faces that react to each other.', 'Kể một câu chuyện nhỏ với hai gương mặt phản ứng với nhau.'),
  ],
  creativity: [
    t('Turn a scribble into something new.', 'Biến một nét nguệch ngoạc thành thứ gì đó mới.'),
    t('Change one big thing in the lesson drawing to make it yours.', 'Đổi một điều lớn trong bài vẽ để nó là của riêng con.'),
    t('Invent a creature that mixes two animals.', 'Sáng tạo một sinh vật lai giữa hai con vật.'),
  ],
  details: [
    t('Add three tiny details to a simple shape.', 'Thêm ba chi tiết nhỏ vào một hình đơn giản.'),
    t('Finish the lesson drawing with small touches that tell a story.', 'Hoàn thiện bài vẽ bằng những nét nhỏ biết kể chuyện.'),
    t('Draw a pocket full of tiny treasures.', 'Vẽ một chiếc túi đầy kho báu tí hon.'),
  ],
};

/** Pick `n` reference drawings that teach `skill`: easiest, middle and hardest. */
function pickDrawings(skill: SkillId, drawings: Drawing[], n: number): Drawing[] {
  const list = drawings.filter((d) => d.skills.includes(skill) && !d.id.startsWith('part-')).sort((a, b) => a.difficulty - b.difficulty || a.id.localeCompare(b.id));
  if (!list.length) return [];
  const out: Drawing[] = [];
  for (let i = 0; i < n; i++) out.push(list[Math.round((i * (list.length - 1)) / Math.max(1, n - 1))]!);
  return out;
}

export function buildSkillJourneys(skills: SkillDef[], drawings: Drawing[]): SkillJourney[] {
  return skills.map((s) => {
    const refs = pickDrawings(s.id, drawings, STAGES.length);
    return {
      id: `journey-${s.id}`,
      skillId: s.id,
      steps: STAGES.map((title, i) => ({
        id: `journey-${s.id}-${i + 1}`,
        title,
        prompt: PROMPTS[s.id][i]!,
        drawingId: refs[i]?.id ?? refs[0]?.id ?? '',
      })),
    };
  });
}
